/** SUNTRONIX ONE — ONE SPACES navigation model.
 *
 *  Each Space is a workspace with its own Context Ribbon.  Every existing route
 *  in the platform is mapped to exactly one canonical Space + Ribbon slot so
 *  power users can reach anything in ~1-2 clicks — no scrolling sidebar.
 */
import {
  Home, Building2, Package, Wrench, DollarSign, BarChart3,
  Settings, LifeBuoy, MonitorSmartphone, Radar, Rocket, FolderLock,
  Sparkles,
  BrainCircuit,
} from "lucide-react";
import { tenantifyNav } from "@/lib/terminology";

/** MY ONE — personal workspace, default for every user. */
export const MY_ONE = {
  id: "my_one",
  label: "My ONE",
  icon: Home,
  landing: "/",
  ribbon: [
    { to: "/",            label: "Action Centre" },
    { to: "/dashboard",   label: "My Dashboard", onlySuntronix: true },
    { to: "/ai",          label: "SUNTRONIX AI" },
    { to: "/copilot",     label: "AI Copilot" },
    { to: "/exception-centre", label: "Exceptions & Escalations", onlySuntronix: true },
    { to: "/notifications", label: "Notifications" },
    { to: "/command-centre", label: "Command Centre", onlySuntronix: true },
    { to: "/home",        label: "OS Home", onlySuntronix: true },
    { to: "/overview",    label: "Overview", onlySuntronix: true },
    { to: "/toolbox",     label: "ONE Toolbox", onlySuntronix: true },
    { to: "/files",       label: "ONE Files", onlySuntronix: true },
    { to: "/vault",       label: "Document Vault", onlySuntronix: true },
    { to: "/vault/inbox", label: "ONE Inbox", onlySuntronix: true },
    { to: "/private-vault", label: "Private Vault", onlySuntronix: true },
    { to: "/ledger",      label: "ONE Ledger" },
    { to: "/work",        label: "My work" },
    { to: "/network",     label: "ONE Network" },
    { to: "/network/templates", label: "Email Templates", onlySuntronix: true },
    { to: "/work/projects", label: "Projects & Tasks" },
    { to: "/approvals/one", label: "Approvals" },
    { to: "/my-devices",  label: "My devices" },
    { to: "/concierge",   label: "Concierge" },
    { to: "/continuity",  label: "Continuity", continuity: true },
    { to: "/relationships", label: "Relationships", relationships: true },
    { to: "/conversations", label: "Conversations" },
    { to: "/whats-new",   label: "What's new" },
    { to: "/roadmap",     label: "Roadmap" },
    { to: "/feedback",    label: "Feedback" },
  ],
};

/** SALES DESK — streamlined internal workspace for the Sales /
 *  Commercial team.  Opens with a New Quotation call-to-action. */
export const SALES_DESK = {
  id: "sales_desk",
  label: "Sales Desk",
  icon: Rocket,
  landing: "/sales-desk",
  onlySuntronix: true,
  ribbon: [
    { to: "/sales-desk",                        label: "Home" },
    { to: "/sales-desk/browse-catalogue", label: "Browse Catalogue" },
    { to: "/sales-desk/collections",      label: "Collections" },
    { to: "/sales-desk/catalogue-enquiries", label: "Catalogue Enquiries" },
    { to: "/commercial/crm/quotations",  label: "Quotations" },
    { to: "/commercial/apple-cto",       label: "Apple CTO" },
    { to: "/commercial/customer-po",     label: "Customer POs" },
    { to: "/commercial/one-orders",         label: "Orders" },
    { to: "/commercial/crm/customers",     label: "Customers" },
    { to: "/commercial/smart-attach",       label: "Smart Attach" },
    { to: "/commercial/one-engage",         label: "ONE Engage" },
    { to: "/commercial/whatsapp-campaigns",  label: "WhatsApp Marketing" },
    { to: "/companies",                            label: "Master companies" },
    { to: "/technology/product-master", label: "Products" },
    { to: "/sales-desk/apple-configurator", label: "Apple CTO/BTO" },
    { to: "/catalogue",                             label: "Rate cards" },
  ],
};

/** SPACES · Suntronix internal. */
export const INSIGHTS_SPACE = {
  id: "insights",
  label: "Insights",
  icon: BarChart3,
  landing: "/insights",
  ribbon: [
    { to: "/insights",                     label: "Executive" },
    { to: "/insights/operations",          label: "Operations" },
    { to: "/insights/technology",          label: "Technology" },
    { to: "/insights/deployment",          label: "Deployment" },
    { to: "/insights/recovery",            label: "Recovery" },
    { to: "/finance/tax-reports",       label: "Tax reports" },
    { to: "/insights/support",             label: "Support" },
    { to: "/insights/inventory",           label: "Inventory" },
    { to: "/insights/reuse",               label: "Reuse & sustainability" },
    { to: "/insights/commercial",          label: "Commercial" },
    { to: "/insights/billing",             label: "Billing" },
    { to: "/insights/courier",             label: "Courier" },
    { to: "/insights/customer-health",     label: "Customer health" },
    { to: "/insights/sla",                 label: "SLA" },
    { to: "/insights/data-quality",        label: "Data Quality" },
    { to: "/insights/catalogue",           label: "Catalogue" },
    { to: "/insights/reports",             label: "Reports" },
  ],
};

export const AIOPS_SPACE = {
  id: "aiops",
  label: "AI Ops",
  icon: BrainCircuit,
  landing: "/aiops",
  ribbon: [
    { to: "/cio",                 label: "CIO Command Center" },
    { to: "/alerts",              label: "Alert Centre" },
    { to: "/ai-recommendations",  label: "AI Recommendations" },
    { to: "/ask-ai",              label: "Ask Suntronix AI" },
    { to: "/aiops",               label: "AI Ops Dashboard" },
    { to: "/aiops/health",        label: "Device Health" },
    { to: "/aiops/service-desk",  label: "AI Service Desk" },
    { to: "/aiops/compliance",    label: "Compliance & Security" },
    { to: "/aiops/warranty",      label: "Warranty Intelligence" },
    { to: "/aiops/lifecycle",     label: "Lifecycle Intelligence" },
    { to: "/aiops/alerts",        label: "Predictive Alerts" },
    { to: "/aiops/remediation",   label: "Automated Remediation" },
    { to: "/aiops/integrations",  label: "Integration Centre" },
  ],
};

export const SPACES_SUNTRONIX = [
  MY_ONE,
  {
    id: "one_ai",
    label: "ONE AI",
    icon: Sparkles,
    landing: "/one-ai",
    onlySuntronix: true,
    ribbon: [
      { to: "/one-ai",             label: "AI Workspace" },
      { to: "/one-ai?tool=ASK_ONE", label: "Ask ONE" },
      { to: "/one-ai?tool=STOCK_AI", label: "Stock AI" },
      { to: "/one-ai?tool=CUSTOMER_AI", label: "Customer AI" },
      { to: "/one-ai?tool=REPORT_AI", label: "Report AI" },
    ],
  },
  SALES_DESK,
  {
    id: "documents",
    label: "Documents",
    icon: FolderLock,
    landing: "/vault",
    onlySuntronix: true,
    ribbon: [
      { to: "/vault", label: "ONE Document Vault" },
      { to: "/vault/inbox", label: "ONE Inbox" },
      { to: "/vault?vault=BUSINESS", label: "Business Vault" },
      { to: "/vault?vault=PERSONAL", label: "Personal Vault" },
      { to: "/vault?vault=FAMILY", label: "Family Vault" },
      { to: "/vault?tab=expiring", label: "Expiring" },
      { to: "/vault?tab=shared", label: "Shared" },
      { to: "/vault?tab=audit", label: "Audit" },
    ],
  },
  {
    id: "customers",
    label: "Customers",
    icon: Building2,
    landing: "/companies",
    ribbon: [
      { to: "/companies",         label: "Companies" },
      { to: "/customers",         label: "Playbook matrix" },
      { to: "/employees",         label: "People" },
      { to: "/admin/sites",       label: "Sites" },
      { to: "/services",          label: "Services" },
      { to: "/governance-centre", label: "Governance & QBR" },
    ],
  },
  {
    id: "technology",
    label: "Technology",
    icon: Package,
    landing: "/assets",
    ribbon: [
      { to: "/technology/product-master", label: "Product Master" },
      { to: "/catalogue-workbench", label: "Catalogue Workbench", onlySuntronix: true },
      { to: "/technology/product-intelligence", label: "Product Intelligence", onlySuntronix: true },
      { to: "/technology/warranty-importer", label: "Warranty Importer", onlySuntronix: true },
      { to: "/technology/product-catalogue", label: "Product Catalogue" },
      { to: "/technology/catalogue-collections", label: "Collections" },
      { to: "/technology/product-content", label: "Product Content" },
      { to: "/technology/apple-india", label: "Apple India Sync" },
      { to: "/technology/image-enrichment", label: "Image Enrichment" },
      { to: "/technology/image-studio", label: "AI Image Studio" },
      { to: "/technology/asset-age-centre", label: "Asset Age Centre" },
      { to: "/technology/catalogue-data-quality", label: "Catalogue Data Quality" },
      { to: "/technology/hsn-enrichment", label: "HSN Enrichment" },
      { to: "/tools/product-format-centre", label: "Product Format Centre", onlySuntronix: true },
      { to: "/tools/hsn-format", label: "HSN Format Tool" },
      { to: "/tools/image-format", label: "Image Format Tool" },
      { to: "/technology/part-number-enrichment", label: "Part Number Enrichment" },
      { to: "/technology/colour-enrichment", label: "Colour Enrichment" },
      { to: "/technology/description-enrichment", label: "AI Descriptions" },
      { to: "/technology/coverage-centre", label: "Sync & Coverage" },
      { to: "/technology/spec-conflicts", label: "Spec Conflicts" },
      { to: "/technology/tech-library",     label: "Tech Library" },
      { to: "/assets",       label: "Assets" },
      { to: "/deployments/inbox", label: "Deployment Inbox", onlySuntronix: true },
      { to: "/warranty",     label: "Warranty" },
      { to: "/warranty-intel", label: "Warranty Intelligence" },
      { to: "/reservations", label: "Reservations" },
      { to: "/loaners",      label: "Loaners" },
      { to: "/rma",          label: "RMA" },
      { to: "/data-quality", label: "Data quality" },
      { to: "/discovery",    label: "Discovery" },
    ],
  },
  {
    id: "operations",
    label: "Operations",
    icon: Wrench,
    landing: "/warehouse",
    ribbon: [
      { to: "/deploy",       label: "Deploy" },
      { to: "/tenant/identity-access", label: "Identity & Access", onlySuntronix: true },
      { to: "/operations/office-batches", label: "Office batches" },
      { to: "/deployment-centre", label: "Control centre" },
      { to: "/bulk-deployment", label: "Bulk deployment" },
      { to: "/offboarding",  label: "Recover" },
      { to: "/pickups",      label: "Pickups" },
      { to: "/shipments",    label: "Shipments" },
      { to: "/warehouse",    label: "Warehouse" },
      { to: "/warehouse/returns", label: "Returns & Redeployment", onlySuntronix: true },
      { to: "/operations/stock-inventory", label: "Stock & Inventory" },
      { to: "/operations/customer-owned-stock", label: "Customer-Owned Stock" },
      { to: "/operations/daily-stock", label: "Daily Stock" },
      { to: "/operations/multibrand-stock", label: "Multi-Brand Stock" },
      { to: "/operations/employee-fulfilment", label: "Employee Fulfilment" },
      { to: "/inspection",   label: "Inspection" },
      { to: "/operations/field-service", label: "Field Service" },
      { to: "/service/rma", label: "RMA & Warranty", onlySuntronix: true },
      { to: "/service",      label: "Support" },
      { to: "/service-desk", label: "ONE Service Desk" },
      { to: "/telephony",    label: "ONE Telephony" },
      { to: "/support",      label: "Support desk" },
      { to: "/operations/apple-services", label: "Apple Services" },
      { to: "/operations/applecare-plus", label: "AppleCare+" },
      { to: "/operations/applecare-requests", label: "AppleCare Requests" },
      { to: "/itad",         label: "ITAD & Recycle" },
      { to: "/control-tower", label: "Control Tower" },
    ],
  },
  {
    id: "commercial",
    label: "Commercial",
    icon: DollarSign,
    landing: "/commercial",
    ribbon: [
      { to: "/commercial",           label: "Overview" },
      { to: "/commercial/one-engage", label: "ONE Engage" },
      { to: "/commercial/whatsapp-campaigns", label: "WhatsApp Marketing" },
      { to: "/commercial/contracts",       label: "Contracts" },
      { to: "/commercial/amc-pms",         label: "AMC / PMS" },
      { to: "/commercial/subscriptions",   label: "Subscriptions & Usage Billing", onlySuntronix: true },
      { to: "/commercial/csat",            label: "CSAT & Feedback" },
      { to: "/commercial/contracts/stubs", label: "Stub cleanup" },
      { to: "/invoices",             label: "Invoices" },
      { to: "/finance/invoice-copies", label: "Invoice copies" },
      { to: "/commercial/buybacks",  label: "Buyback credit" },
      { to: "/commercial/quotes",    label: "Quotes" },
      { to: "/catalogue",            label: "Rate cards" },
      { to: "/marketplace",          label: "Marketplace" },
      { to: "/catalogue-v2",         label: "Visual catalogue" },
    ],
  },
  {
    id: "insights",
    label: "Insights",
    icon: BarChart3,
    landing: "/insights",
    ribbon: [
      { to: "/insights",                 label: "Executive" },
      { to: "/insights/operations",      label: "Operations" },
      { to: "/insights/technology",      label: "Technology" },
      { to: "/insights/deployment",      label: "Deployment" },
      { to: "/insights/recovery",        label: "Recovery" },
      { to: "/insights/support",         label: "Support" },
      { to: "/insights/inventory",       label: "Inventory" },
      { to: "/insights/reuse",           label: "Reuse & sustainability" },
      { to: "/insights/commercial",      label: "Commercial" },
      { to: "/insights/billing",         label: "Billing" },
      { to: "/insights/courier",         label: "Courier" },
      { to: "/insights/customer-health", label: "Customer health" },
      { to: "/insights/sla",             label: "SLA" },
      { to: "/insights/data-quality",    label: "Data Quality" },
      { to: "/insights/catalogue",       label: "Catalogue" },
      { to: "/insights/reports",         label: "Reports" },
    ],
  },
  {
    id: "legacy-insights",
    label: "Legacy analytics",
    icon: BarChart3,
    landing: "/reports",
    ribbon: [
      { to: "/reports",          label: "Reports" },
      { to: "/scenario-lab",     label: "Scenario Lab" },
      { to: "/economics",        label: "Economics" },
      { to: "/circularity",      label: "Circularity" },
      { to: "/refresh-engine",   label: "Refresh" },
      { to: "/benchmarks",       label: "Benchmarks" },
      { to: "/audit",            label: "Audit Trail" },
    ],
  },
  {
    id: "admin",
    label: "Administration",
    icon: Settings,
    landing: "/admin/control-centre",
    onlySuntronix: true,
    ribbon: [
      { to: "/admin/control-centre", label: "Control centre" },
      { to: "/admin/role-dashboards", label: "Role Dashboards", onlySuntronix: true },
      { to: "/admin/automation", label: "Automation Engine", onlySuntronix: true },
      { to: "/audit", label: "Audit Trail", onlySuntronix: true },
      { to: "/admin/integrations", label: "Integration Centre", onlySuntronix: true },
      { to: "/aiops", label: "AI Ops (Advanced)", onlySuntronix: true },
      { to: "/cio", label: "CIO Command Center", onlySuntronix: true },
      { to: "/admin/one-control-centre", label: "ONE Control Centre" },
      { to: "/admin/establishments", label: "Establishments" },
      { to: "/admin/terms-conditions", label: "Terms & Conditions" },
      { to: "/admin/quotation-template", label: "Quotation Template" },
      { to: "/admin/quote-disclaimer", label: "Quote Disclaimer", onlySuntronix: true },
      { to: "/admin/honorifics", label: "Honorifics (Master Data)", onlySuntronix: true },
      { to: "/admin/typography", label: "Brand & Communication", onlySuntronix: true },
      { to: "/administration/one-studio", label: "ONE Studio", onlySuntronix: true },
      { to: "/admin/tenant-catalogue", label: "Tenant Catalogue", onlySuntronix: true },
      { to: "/administration/config-studio", label: "Configuration Studio", onlySuntronix: true },
      { to: "/admin/privacy-dpdp", label: "Privacy & DPDP", onlySuntronix: true },
      { to: "/admin/document-templates", label: "Document Templates" },
      { to: "/admin/login-experience", label: "Login Experience" },
      { to: "/admin/bank-details", label: "Bank Details" },
      { to: "/admin/customer-prefix-cleanup", label: "Prefix Cleanup" },
      { to: "/admin/group-dashboard", label: "Group Dashboard" },
      { to: "/one-post", label: "ONE POST", financeVault: true },
      { to: "/finance/one-ledger", label: "ONE Ledger" },
      { to: "/finance/tally-reconciliation", label: "Tally Reconciliation" },
      { to: "/finance/compliance-bridge", label: "Tax & Compliance", onlySuntronix: true },
      { to: "/finance/cash-control", label: "Cash Control", financeVault: true },
      { to: "/finance/cards", label: "Card Control", financeVault: true },
      { to: "/admin/communications", label: "Communications" },
      { to: "/admin/whatsapp-control", label: "WhatsApp API Control" },
      { to: "/admin/onboard-tenant", label: "Onboard tenant" },
      { to: "/admin/sso",            label: "SSO" },
      { to: "/admin/locked-accounts", label: "Locked accounts" },
      { to: "/admin/one-sms", label: "ONE SMS" },
      { to: "/admin/public-reference", label: "Public Reference" },
      { to: "/admin/finance-vault", label: "Finance Vault", financeVault: true },
      { to: "/admin/security-centre", label: "Security Centre", securityCentre: true },
      { to: "/admin/legal",          label: "Legal agreements" },
      { to: "/admin/data-quality",   label: "Data Quality" },
      { to: "/admin/product-classification", label: "Product Classification" },
      { to: "/admin/mapping-review", label: "Mapping Review" },
      { to: "/admin/exceptions",     label: "Exceptions" },
      { to: "/admin/integration-health", label: "Integration Health" },
      { to: "/admin/demo-console", label: "Demo Console", onlySuntronix: true },
      { to: "/admin/tenant-readiness", label: "Tenant Readiness", onlySuntronix: true },
      { to: "/admin/preflight-rules",     label: "Preflight Rules" },
      { to: "/admin/preflight-approvals", label: "Preflight Approvals" },
      { to: "/admin/couriers",       label: "Courier partners" },
      { to: "/admin/location-inventory", label: "Location inventory" },
      { to: "/admin/production-readiness", label: "Production readiness" },
      { to: "/admin/one-connect", label: "ONE CONNECT" },
      { to: "/admin/one-connect/india-tax", label: "India Tax & Compliance", onlySuntronix: true },
      { to: "/admin/one-connect/control-centre", label: "ONE CONNECT 3.0" },
      { to: "/admin/one-connect/apple-business", label: "Apple Business" },
      { to: "/admin/one-connect/telephony", label: "Telephony" },
      { to: "/admin/one-connect/intake", label: "Service Intake" },
      { to: "/admin/one-connect/tally", label: "Tally" },
      { to: "/admin/documents/authorised-signers", label: "Authorised Signers (ONE SIGN)" },
      { to: "/admin/one-connect/email-intake", label: "Email Intake (ONE Inbox)" },
      { to: "/admin/one-connect/product-content", label: "Product Content" },
      { to: "/admin/one-connect/free", label: "Free & open connectors" },
      { to: "/admin/integration-readiness", label: "Integration Readiness" },
      { to: "/admin/tenant-branding", label: "Tenant branding" },
      { to: "/admin/welcome-labels", label: "Welcome labels & tips", onlySuntronix: true },
      { to: "/settings/welcome-label-branding", label: "Welcome label branding" },
      { to: "/insights/security/risk-intelligence", label: "Risk intelligence" },
      { to: "/admin/platform-foundation", label: "Platform foundation" },
      { to: "/admin/studio",         label: "Platform studio" },
      { to: "/admin/marketplace",    label: "Marketplace" },
      { to: "/users",                label: "Users & roles", userAdmin: true },
      { to: "/location-master",      label: "Locations" },
      { to: "/imports",              label: "Imports" },
      { to: "/security",             label: "Security" },
      { to: "/trust",                label: "Trust" },
      { to: "/sop",                  label: "SOP Centre" },
      { to: "/knowledge",            label: "Knowledge" },
      { to: "/automation",           label: "Automation" },
      { to: "/intake",               label: "Intake" },
    ],
  },
];

/** SPACES · Customer users — simpler set, no operational complexity.
 *  tenantifyNav enforces the GLOBAL TENANT TERMINOLOGY STANDARD:
 *  every "My …" label automatically becomes "Our …" for tenants. */
export const SPACES_CUSTOMER = tenantifyNav([
  MY_ONE,
  AIOPS_SPACE,
  {
    id: "technology",
    label: "Technology",
    icon: Package,
    landing: "/our-technology",
    ribbon: [
      { to: "/our-technology", label: "Command Centre" },
      { to: "/employee-hub", label: "Employee Hub", onlyEmployee: true },
      { to: "/assets",    label: "Assets" },
      { to: "/inventory", label: "My inventory" },
      { to: "/employees", label: "People" },
      { to: "/warehouse", label: "Inventory" },
      { to: "/catalogue", label: "Catalogue" },
      { to: "/warranty",  label: "Warranty" },
      { to: "/warranty-intel", label: "Warranty Intelligence" },
      { to: "/alerts", label: "Alert Centre" },
      { to: "/ai-recommendations", label: "AI Recommendations" },
    ],
  },
  {
    id: "services",
    label: "Services",
    icon: LifeBuoy,
    landing: "/services",
    ribbon: [
      { to: "/services",    label: "Service catalogue" },
      { to: "/service-hub", label: "Service Hub" },
      { to: "/our-contracts", label: "Our Contracts" },
      { to: "/our-rma", label: "Our RMA" },
      { to: "/our-subscriptions", label: "Our Subscriptions" },
      { to: "/our-feedback", label: "Our Feedback" },
      { to: "/support/my-requests", label: "My Requests" },
      { to: "/deploy",      label: "Deploy" },
      { to: "/pickups",     label: "Recover" },
      { to: "/offboarding", label: "Offboard" },
      { to: "/operations/apple-services", label: "Apple Services" },
      { to: "/operations/applecare-plus", label: "AppleCare+" },
      { to: "/operations/applecare-requests", label: "AppleCare Requests" },
      { to: "/requests",    label: "Requests" },
    ],
  },
  {
    id: "commercial",
    label: "Invoices",
    icon: DollarSign,
    landing: "/invoices",
    ribbon: [
      { to: "/invoices",  label: "My invoices" },
      { to: "/invoices?tab=ageing",  label: "Ageing" },
      { to: "/my-orders", label: "My orders" },
    ],
  },
  {
    id: "insights",
    label: "Insights",
    icon: BarChart3,
    landing: "/insights",
    ribbon: [
      { to: "/insights",                 label: "Executive" },
      { to: "/insights/operations",      label: "Operations" },
      { to: "/insights/technology",      label: "Technology" },
      { to: "/insights/deployment",      label: "Deployment" },
      { to: "/insights/recovery",        label: "Recovery" },
      { to: "/insights/support",         label: "Support" },
      { to: "/insights/inventory",       label: "Inventory" },
      { to: "/insights/reuse",           label: "Reuse & sustainability" },
      { to: "/insights/customer-health", label: "Health" },
      { to: "/insights/sla",             label: "SLA" },
      { to: "/insights/data-quality",    label: "Data Quality" },
      { to: "/insights/reports",         label: "Reports" },
    ],
  },
]);

/** Reverse index — pathname → space id. */
function buildPathIndex(spaces) {
  const idx = {};
  for (const s of spaces) for (const r of s.ribbon) idx[r.to] = s.id;
  return idx;
}
const IDX_SUNTRONIX = buildPathIndex(SPACES_SUNTRONIX);
const IDX_CUSTOMER  = buildPathIndex(SPACES_CUSTOMER);

/** SUB-SPACES — a destination that opens its own second-bar context with a
 *  back arrow to its parent Space. Purely additive to the Space model. */
export const SUBSPACES = [
  {
    id: "documents",
    label: "Documents",
    icon: FolderLock,
    parentSpaceId: "my_one",
    parentLanding: "/",
    onlySuntronix: true,
    // active whenever the operator is anywhere under the Document Vault
    match: (path) => path === "/vault" || path.startsWith("/vault"),
    ribbon: [
      { to: "/vault",                label: "ONE Document Vault" },
      { to: "/vault?vault=BUSINESS", label: "Business Vault" },
      { to: "/vault?vault=PERSONAL", label: "Personal Vault" },
      { to: "/vault?vault=FAMILY",   label: "Family Vault" },
      { to: "/vault/inbox",          label: "ONE Inbox" },
    ],
  },
];

export function subspaceForPath(pathname, isCustomer) {
  if (isCustomer) return null;   // sub-spaces are Suntronix-internal for now
  return SUBSPACES.find((s) => s.match(pathname)) || null;
}

export function spaceIdForPath(pathname, isCustomer) {
  const idx = isCustomer ? IDX_CUSTOMER : IDX_SUNTRONIX;
  if (idx[pathname]) return idx[pathname];
  for (const key of Object.keys(idx)) {
    if (key !== "/" && pathname.startsWith(key + "/")) return idx[key];
  }
  return "my_one";
}

/** Smart default landing space per role (§38). */
export function defaultSpaceForRole(role) {
  if (!role) return "my_one";
  // Sales / Commercial roles land on the SALES DESK.
  if (role === "sales_coordinator"
      || role === "customer_experience") return "sales_desk";
  if (role === "warehouse" || role === "stores") return "operations";
  if (role === "finance" || role === "customer_finance") return "commercial";
  if (role === "customer_it_admin") return "technology";
  if (role === "customer_employee") return "my_one";
  return "my_one";
}
